import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import {
  FilterPrimaryUseClassItems,
  getCountriesForFilters,
  getMarketsFilters,
  getPropertyTypesWithMixedUseForFilters,
  getPropertyUsesForFilters,
  PROPERTY_TYPE_KEY,
} from '@/bundles/GroupFilters/consts';
import { IFilterButtonGroupItems } from '@/bundles/GroupFilters/interfaces/inputs/IFilterButtonGroupItems';
import { FilterBusinessLineAll, FilterBusinessLineItems } from '@/bundles/GroupFilters/enums/FilterBusinessLineEnum';
import {
  FilterConsultingButtonItems,
  FilterInvestmentSaleButtonItems,
  FilterSubleaseButtonItems,
  FilterTransactionTypeLabels
} from '@/bundles/GroupFilters/enums/transactionType';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import { getSaleConditionsTextValues } from '@/bundles/Comparables/enums/SaleConditionsEnum';
import { getPropertyCenterTypeTextValues } from '@/bundles/Property/enums/PropertyCenterTypeEnum';
import { Nullable } from '@/utils/types';
import { getSelectItems } from '@/bundles/Pipelines/helpers';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';
import {
  FilterReferralSendTypeEnum,
  FilterReferralSendTypeItems
} from '@/bundles/GroupFilters/enums/FilterReferralSendTypeEnum';
import { ContactRolesEnum } from '@/bundles/Contact/enums/contactRoles';
import { getContactRolesBySource } from '@/bundles/Contact/helpers/cards/contactsCard';
import { isYearRule } from '@/bundles/GroupFilters/helpers/rules';
import { FilterPrimaryUseClassEnum } from '@/bundles/GroupFilters/types';
import { IGroupFilterItem } from '@/bundles/GroupFilters/helpers';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';

export const getDealFilters = (filters?: Nullable<IGroupFilterItem>): IFilterConfigItem[] => {
  const propertyTypesWithMixedUse = getPropertyTypesWithMixedUseForFilters();
  const selectedPropertyTypes = filters?.[PROPERTY_TYPE_KEY]?.value || [];

  return [
    // FilterCategoryEnum.deal_info start
    {
      title: i18n.t(LocalizationEnum.business_line) as string,
      name: 'deal_business_line',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterBusinessLineItems,
      defaultValue: FilterBusinessLineAll,
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Transaction Type',
      type: FilterTypeEnum.transaction_type_group,
      category: FilterCategoryEnum.deal_info,
      children: [
        {
          title: 'Transaction Type',
          name: 'deal_transaction_type',
          items: <IFilterButtonGroupItems>Object.keys(FilterTransactionTypeLabels).map((key) => ({
            text: FilterTransactionTypeLabels[key],
            value: key,
          })),
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
        {
          title: 'Sublease',
          name: 'deal_sublease',
          items: <IFilterButtonGroupItems>FilterSubleaseButtonItems,
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
        {
          title: 'Investment Sale',
          name: 'deal_investment',
          items: <IFilterButtonGroupItems>FilterInvestmentSaleButtonItems,
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
        {
          title: 'Consulting',
          name: 'deal_consulting',
          items: <IFilterButtonGroupItems>FilterConsultingButtonItems,
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
      ],
    },
    {
      title: 'Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.deal_info,
      children: [
        {
          title: 'Property Type',
          name: PROPERTY_TYPE_KEY,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Property Type As Primary Use',
          name: 'property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: i18n.t(LocalizationEnum.sale_conditions) as string,
      items: getSaleConditionsTextValues(),
      name: 'deal_sale_conditions',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.deal_info,
      children: [
        {
          title: 'Property Use Class',
          name: 'property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Property Use',
          name: 'property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Property Use Type(s)',
      name: 'property_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: i18n.t(LocalizationEnum.retail_center_type) as string,
      items: getPropertyCenterTypeTextValues(),
      name: 'property_center_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Transaction Date',
      name: 'deal_close_transaction_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Transaction Year',
      name: 'deal_close_year',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.deal_info,
      additional: {
        minRules: [isYearRule],
        maxRules: [isYearRule],
        isRawFormat: true,
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: 'Transaction Category',
      name: 'deal_category',
      type: FilterTypeEnum.transaction_category_checkbox_group,
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Deal Size',
      name: 'deal_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.deal_info,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Land Size',
      name: 'deal_land_size',
      type: FilterTypeEnum.land_size_range, // sending with deal_land_size_unit
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Units / Keys',
      name: 'deal_units',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Deal Status',
      name: 'deal_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: [
        { text: 'Pending', value: 'pending' },
        { text: 'Approved', value: 'approved' },
        { text: 'Invoiced', value: 'invoiced' },
        { text: 'Paid', value: 'paid' }
      ],
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Contract Pending Status',
      name: 'deal_contract_pending',
      type: FilterTypeEnum.one_select,
      items: [
        { text: 'All Deals', value: 'all' },
        { text: 'Contract Pending Only', value: 'pending' },
        { text: 'Exclude Contract Pending', value: 'notPending' }
      ],
      category: FilterCategoryEnum.deal_info,
    },
    {
      title: 'Brokers',
      name: 'deal_broker',
      type: FilterTypeEnum.brokers_select,
      category: FilterCategoryEnum.deal_info,
    },
    // FilterCategoryEnum.deal_info end
    // FilterCategoryEnum.payment_details start
    {
      title: 'Total Consideration',
      name: 'deal_total_consideration',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.payment_details,
    },
    {
      title: 'In-House Gross',
      name: 'deal_inhouse_gross',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.payment_details,
    },
    {
      title: 'Commission %',
      name: 'deal_commission_percentage',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.payment_details,
      additional: {
        suffix: '%',
      },
      formatter: (v: Nullable<number>) => isValuePresent(v) ? `${v}%` : '',
    },
    {
      title: 'Is Unpaid',
      name: 'deal_is_unpaid',
      type: FilterTypeEnum.switch,
      category: FilterCategoryEnum.payment_details,
    },
    {
      title: 'Paid Amount',
      name: 'deal_paid_amount',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.payment_details,
    },
    {
      title: 'Due Date',
      name: 'deal_invoice_due_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.payment_details,
    },
    {
      title: 'Paid Date',
      name: 'deal_close_paid_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.payment_details,
    },
    {
      title: 'Due Amount',
      name: 'deal_due_amount',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.payment_details,
    },
    {
      title: 'Invoice Amount',
      name: 'deal_invoice_amount',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.payment_details,
    },
    {
      title: 'Invoice Date',
      name: 'deal_invoice_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.payment_details,
    },
    // FilterCategoryEnum.payment_details end
    // FilterCategoryEnum.referrals start
    {
      title: 'Referral Send Type',
      name: 'deal_inbound_outbound',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterReferralSendTypeItems,
      defaultValue: FilterReferralSendTypeEnum.all,
      category: FilterCategoryEnum.referrals,
    },
    {
      title: 'Referral Contact Keyword',
      name: 'deal_referral_contact_keyword',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.referrals,
    },
    {
      title: 'Referral Company Keyword',
      name: 'deal_referral_company_keyword',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.referrals,
    },
    // FilterCategoryEnum.referrals end
    // FilterCategoryEnum.pipeline start
    {
      title: 'Pipeline Source Category',
      name: 'pipeline_source',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getSelectItems().sources,
      category: FilterCategoryEnum.pipeline,
    },
    {
      title: `Pipeline ${i18n.t(LocalizationEnum.business_line)}`,
      name: 'pipeline_business_line',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterBusinessLineItems,
      defaultValue: FilterBusinessLineAll,
      category: FilterCategoryEnum.pipeline,
    },
    // FilterCategoryEnum.pipeline end
    // FilterCategoryEnum.market_location start
    {
      title: 'Address',
      name: 'property_address',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'City',
      name: 'property_location_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.market_location,
      additional: {
        citiesSource: 'property',
      }
    },
    {
      title: 'State',
      name: 'property_location_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Country',
      name: 'property_location_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Zip / Postal Code',
      name: 'property_location_postal_cd',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.market_location,
    },
    ...getMarketsFilters({ propertyTypes: selectedPropertyTypes }),
    {
      title: `Property ${i18n.t(LocalizationEnum.apn_number)}`,
      name: 'property_apn_number',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    // FilterCategoryEnum.market_location end
    // FilterCategoryEnum.company_contact start
    {
      title: 'Contact Name',
      name: 'contact_names',
      type: FilterTypeEnum.contact_names_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Deal Role',
      name: 'contact_deal_role',
      items: getContactRolesBySource('deal'),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Company Name',
      name: 'company_name',
      type: FilterTypeEnum.company_name_combobox,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Company Deal Role',
      name: 'company_deal_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: [
        { text: 'Buyer', value: ContactRolesEnum.buyer },
        { text: 'Seller', value: ContactRolesEnum.seller },
        { text: 'Tenant', value: ContactRolesEnum.tenant },
        { text: 'Landlord', value: ContactRolesEnum.landlord },
      ],
      category: FilterCategoryEnum.company_contact,
    },
    // FilterCategoryEnum.company_contact end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Edited Date',
      name: 'deal_view_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'deal_created_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'deal_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'deal_view',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'deal_created_by',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'deal_edited_by',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('deal'),
  ].filter(Boolean) as IFilterConfigItem[]; // filter out null values
};
